import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { PortalApiError, toPortalProblem } from './portalProblem';
import { handleSessionExpired } from '../auth/portalSession';

/**
 * The Portal app's dedicated axios instance — deliberately NOT the CRM's
 * shared `@/shared/api/client` `apiClient` (frontend-security-auditor +
 * typescript-critic, swarm review, matching the exact precedent
 * `dealStatusApi.ts` already established for this reason): that client
 * forwards a Cognito bearer token, assumes the CRM's own origin, and carries
 * CRM-specific OTel/trace-header wiring. This Portal authenticates with an
 * HttpOnly session cookie on whatever origin the BFF actually lives on.
 *
 * Corrected (PP-9.6): this comment used to claim `baseURL: ''` (relative)
 * was correct because requirements §3.4 put the Portal UI and its BFF on
 * the SAME domain in every real deployment -- true only briefly (D22's
 * first draft, reversed the same day it was written). `deals.ceqstudio.com`
 * (the Portal UI) and `api.deals.ceqstudio.com` (this BFF) are now TWO
 * SEPARATE origins in every real deployment -- exactly the gap PP-9.1's own
 * swarm review flagged and this story's CORS fix
 * (`backend/services/bff/app/main.py`) closes server-side. A relative
 * `baseURL` would send every request to the FRONTEND's own origin (nothing
 * listening there) once both domains are live, so this is now build-time
 * configurable: `VITE_BFF_BASE_URL` set to the absolute
 * `https://api.deals.ceqstudio.com` origin in the real deployment pipeline,
 * left unset (relative) for local dev, where `vite.config.ts`'s own
 * dev-server proxy already reproduces the same-origin illusion.
 * `withCredentials: true` still carries the session cookie cross-origin --
 * that's what this story's `allow_credentials=True` CORS config permits.
 */
const rawBffBaseUrl = import.meta.env['VITE_BFF_BASE_URL'];

/** Exported so a full-page navigation (e.g. the staff Entra sign-in
 * redirect, which cannot be an XHR) can target the same configured origin
 * this axios instance uses — see `auth/SignInPage.tsx`. */
export const bffBaseUrl: string = typeof rawBffBaseUrl === 'string' ? rawBffBaseUrl : '';

export const portalApiClient: AxiosInstance = axios.create({
  baseURL: bffBaseUrl,
  withCredentials: true,
});

portalApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (!error.response) {
      // Network/transport failure (no HTTP response at all) -- propagate the
      // original AxiosError untouched; there is no status/body to build a
      // PortalProblem from.
      return Promise.reject(error);
    }
    const problem = toPortalProblem(error.response.data, error.response.status);
    const portalError = new PortalApiError(problem);

    // Only a CONFIRMED expired/invalid session trips the global handler --
    // never a wrong-OTP-code 401 (code: "invalid_code"/"not_eligible"),
    // which has nothing to do with an established session (state-management-
    // critic + skeptic, swarm review).
    if (portalError.status === 401 && portalError.code === 'session_expired') {
      handleSessionExpired();
    }

    return Promise.reject(portalError);
  },
);
